import { Row, Col, Icon } from '@workramp/ui';
import SkeletonPulse from '~/components/common/v2/skeleton-loader';
import { useUserProfileQuery } from '../../api';
import { LEARNER_ROUTES } from '../../constants';
import { LearnerAvatar } from '../LearnerAvatar';
import {
  ProfilePictureWrapper,
  ProfileSecondaryText,
  ProfileWrapper,
  ProfileNameLink,
  ProfileName,
  ViewPictureOverlay,
} from './Sidebar.styles';

const { I18n, getCurrentUser } = window;

const TRANSLATED_GREETING = I18n.t('common.greetings.hi');
const TRANSLATED_VIEW_PROFILE = I18n.t('js.learner.home.sidebar.view_profile');
const TRANSLATED_PROFILE_LINK_LABEL = (firstName) =>
  I18n.t('js.learner.home.sidebar.profile_link_label', { firstName });

export interface SidebarProfileProps {
  userId: number;
  brandColor?: string;
}

export function SidebarProfile({ userId, brandColor }: SidebarProfileProps) {
  const profileQuery = useUserProfileQuery(userId, {
    placeholderData() {
      const { id, avatar, displayName, name, preferredName, email } = getCurrentUser();
      return {
        id,
        avatar,
        displayName,
        name,
        preferredName,
        email,
        bio: null,
        numSecondsSpentOnGuides: 0,
        pronouns: null,
        title: null,
        badges: [],
        trainingStats: {
          guidesAssigned: 0,
          guidesCompleted: 0,
        },
      };
    },
  });

  const { title, displayName = '', avatar = '', badges = [] } = profileQuery.data ?? {};

  const firstName = displayName.split(' ')[0];

  // Default to showing learner role when available otherwise use number of badges
  let description = title;
  if (!description && badges.length > 0) {
    description = window.I18n.t('js.learner.badges.num_badges', {
      count: badges.length,
    });
  }

  const handleEdit = () => {
    window.location.href = LEARNER_ROUTES.profile;
  };

  return (
    <ProfileWrapper>
      <ProfilePictureWrapper
        onPress={handleEdit}
        tooltip={{ message: TRANSLATED_VIEW_PROFILE, delay: 0, placement: 'right' }}
        aria-label={TRANSLATED_VIEW_PROFILE}
      >
        <LearnerAvatar name={displayName} imgUrl={avatar} brandColor={brandColor} size="xl" />
        <ViewPictureOverlay>
          {' '}
          <Icon name="eye-open:regular" />{' '}
        </ViewPictureOverlay>
      </ProfilePictureWrapper>

      <Row>
        <Col>
          <ProfileNameLink href={LEARNER_ROUTES.profile} aria-label={TRANSLATED_PROFILE_LINK_LABEL(firstName)}>
            <ProfileName weight="semibold" density="xl">
              {TRANSLATED_GREETING}, {firstName}
            </ProfileName>
          </ProfileNameLink>
        </Col>
      </Row>

      <Row justify="center">
        <Col xs="auto">
          {profileQuery.isPlaceholderData && profileQuery.isRefetching && <SkeletonPulse width={100} height={24} />}
          {description && (
            <ProfileSecondaryText size={1} density="xl">
              {description}
            </ProfileSecondaryText>
          )}
        </Col>
      </Row>
    </ProfileWrapper>
  );
}
